import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { SelfServeTier } from '@/lib/marketing';

const STRIPE_PUBLISHABLE_KEY = 'pk_live_51J1PlkHhjncHjxc7Wmua97kzLw2r8VUGPO8i8iXhU9lMR0yVsNkRY4BoHDRQUGDN54QVjK0be7ioaaVD8G5ghX5D00O5DiqLdA';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

function CheckoutForm({
  tier,
  clientSecret,
  onSuccess,
}: {
  tier: SelfServeTier;
  clientSecret: string;
  onSuccess: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(root.classList.contains('dark'));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const cardElementStyle = useMemo(
    () => ({
      base: {
        fontSize: '16px',
        color: isDark ? '#F8FAFC' : '#111827',
        iconColor: isDark ? '#E2E8F0' : '#64748B',
        '::placeholder': {
          color: isDark ? '#CBD5E1' : '#6B7280',
        },
      },
      invalid: {
        color: '#EF4444',
        iconColor: '#EF4444',
      },
    }),
    [isDark],
  );

  const handlePaymentError = (error: any) => {
    const message = error?.message || 'An unexpected payment error occurred.';
    setErrorMessage(message);
    toast({
      title: 'Payment failed',
      description: message,
      variant: 'destructive',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setErrorMessage('Payment form is not ready yet.');
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        handlePaymentError(error);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        toast({
          title: 'Package purchased',
          description: `${tier.name} is now available in your dashboard as a one-time training credit.`,
        });
        onSuccess();
      }
    } catch (error: any) {
      handlePaymentError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Included in this package</p>
        <ul className="mt-3 space-y-2">
          {tier.inclusions.slice(0, 5).map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm leading-6 text-foreground/90">
              <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-md border border-input bg-background px-3 py-3">
        <CardElement options={{ style: cardElementStyle }} />
      </div>

      {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}

      <Button type="submit" className="w-full" disabled={loading || !stripe || !elements}>
        {loading ? 'Processing payment...' : `Pay ${tier.price}`}
      </Button>
      <p className="text-xs leading-5 text-muted-foreground">
        This is a one-time training package purchase. After you use the credit to launch a training session, it will be
        tracked in your dashboard and expire when that training finishes.
      </p>
    </form>
  );
}

export default function TrainingPackageCheckout({
  tier,
  onSuccess,
}: {
  tier: SelfServeTier;
  onSuccess: () => void;
}) {
  const { user } = useAuth();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const createPaymentIntent = async () => {
      if (!user) {
        setError('You must be signed in to purchase a training package.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase.functions.invoke('create-payment-intent', {
          body: {
            plan_id: tier.id,
            user_id: user.id,
          },
        });

        if (error || !data?.clientSecret) {
          throw new Error(data?.error || error?.message || 'Failed to initialize payment.');
        }

        setClientSecret(data.clientSecret);
      } catch (error: any) {
        setError(error.message || 'Failed to initialize payment.');
      } finally {
        setLoading(false);
      }
    };

    void createPaymentIntent();
  }, [tier.id, user]);

  if (loading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">Initializing secure payment...</div>;
  }

  if (error || !clientSecret) {
    return <div className="py-4 text-sm text-destructive">{error || 'Could not initialize payment.'}</div>;
  }

  return (
    <Elements options={{ clientSecret, appearance: { theme: 'stripe' } }} stripe={stripePromise}>
      <CheckoutForm tier={tier} clientSecret={clientSecret} onSuccess={onSuccess} />
    </Elements>
  );
}
